// ============================================================
// testesFisicos.js — a bateria de testes e a evolução entre coletas
//
// A antropometria vive em `avaliacaoFisica.js`. Aqui ficam os testes de
// desempenho: salto, abdominal, circuito, flexibilidade, tempo de pista.
// São dois arquivos porque são duas coisas diferentes: o salto do atleta
// melhora com treino em semanas, e o somatotipo dele muda em meses.
//
// ── A bateria não é fixa ────────────────────────────────────
//
// A lista de testes mudou entre 2025 e 2026 e vai mudar de novo na
// próxima avaliação. Por isso os testes são dados, numa tabela que se
// edita, e não um `if` por teste espalhado pelas telas e pelo PDF.
// Acrescentar um teste é acrescentar uma linha em `TESTES`.
//
// ── A direção de cada teste ─────────────────────────────────
//
// É a parte que uma planilha não consegue guardar e que erra em
// silêncio. Em impulsão horizontal, 20 cm a mais é melhora. No circuito,
// 2 segundos a mais é piora, e a mesma fórmula de "evolução %" aplicada
// aos dois devolve um sinal certo e um errado. Na planilha de agosto de
// 2026 convivem na mesma aba testes de centímetros, de repetições e de
// segundos, somados na mesma "Média de evolução".
//
// ── As marcas que não são número ────────────────────────────
//
// A planilha usa "x", "X", "F", "DM" e "-" onde não houve resultado. São
// informação, não lixo: "F" é falta e "DM" é dispensa médica, e as duas
// mudam o que o relatório diz sobre o atleta. O que elas não podem fazer
// é entrar numa média valendo zero, porque zero é um resultado péssimo e
// ausência não é resultado.
// ============================================================

// ── As marcas de ausência ────────────────────────────────────

export const AUSENCIAS = {
  x: { codigo: "naoRealizado", rotulo: "Não realizado" },
  f: { codigo: "falta", rotulo: "Falta" },
  dm: { codigo: "dispensaMedica", rotulo: "Dispensa médica" },
  "-": { codigo: "semRegistro", rotulo: "Sem registro" },
  "": { codigo: "semRegistro", rotulo: "Sem registro" },
};

// Lê uma célula da planilha.
//
// Devolve sempre a mesma forma: `{ valor, ausencia }`, com um dos dois
// preenchido. Quem consome não precisa saber se a planilha escreveu "X"
// ou "x" ou deixou vazio, e nunca recebe um número que não foi medido.
export function lerResultado(bruto) {
  if (bruto === null || bruto === undefined) {
    return { valor: null, ausencia: AUSENCIAS[""].codigo };
  }
  if (typeof bruto === "number") {
    return Number.isFinite(bruto) ? { valor: bruto, ausencia: null } : { valor: null, ausencia: "semRegistro" };
  }
  const texto = String(bruto).trim();
  const marca = AUSENCIAS[texto.toLowerCase()];
  if (marca) return { valor: null, ausencia: marca.codigo };

  // Vírgula decimal, que é como a planilha brasileira escreve.
  const n = Number(texto.replace(/\s/g, "").replace(",", "."));
  if (Number.isFinite(n)) return { valor: n, ausencia: null };
  // Qualquer outro texto é tratado como ausência e não como zero. Uma
  // anotação que ninguém previu ("rep", "não fez") valendo 0 derrubaria
  // a média da modalidade inteira.
  return { valor: null, ausencia: "semRegistro" };
}

// ── Três tentativas, vale a melhor ───────────────────────────
//
// Todo teste é feito em três tentativas e vale a melhor: a maior
// distância no salto, o menor tempo no sprint. A planilha guarda só o
// resultado escolhido, e as outras duas ficam no papel da coleta.
//
// O sistema guarda as três porque elas respondem uma pergunta que a
// melhor sozinha não responde: se o atleta saltou 184, 173 e 176, a
// melhor é 184 e a dispersão é de 11 cm — num grupo em que a evolução
// média entre coletas é de 4%, isso diz que a melhora pode ser da
// tentativa boa e não do treino. Com a melhor isolada ninguém tem como
// desconfiar disso.
//
// Qual é a melhor depende da direção do teste, e é o mesmo cuidado de
// `evolucao`: em segundos, a melhor é a menor.
export function melhorTentativa(tentativas, teste) {
  const lidas = (Array.isArray(tentativas) ? tentativas : [tentativas]).map(lerResultado);
  const valores = lidas.map(l => l.valor).filter(v => v !== null);
  if (!valores.length) {
    // Nenhuma tentativa com número. A ausência que vale é a primeira
    // informativa: "falta" e "dispensa médica" dizem mais que vazio.
    const informativa = lidas.find(l => l.ausencia !== "semRegistro");
    return {
      valor: null,
      ausencia: informativa ? informativa.ausencia : "semRegistro",
      tentativas: valores,
      amplitude: null,
    };
  }
  const melhor =
    teste && teste.direcao === "menor" ? Math.min(...valores) : Math.max(...valores);
  return {
    valor: melhor,
    ausencia: null,
    tentativas: valores,
    // A diferença entre a melhor e a pior tentativa válida. Serve para o
    // relatório marcar coleta instável sem ninguém reabrir o papel.
    amplitude:
      valores.length > 1 ? arred(Math.max(...valores) - Math.min(...valores), 2) : 0,
  };
}

// Resolve uma célula que pode ser um valor só ou as três tentativas.
export function resolverColeta(bruto, teste) {
  if (Array.isArray(bruto)) return melhorTentativa(bruto, teste);
  const lido = lerResultado(bruto);
  return { ...lido, tentativas: lido.valor === null ? [] : [lido.valor], amplitude: 0 };
}

// ── A bateria ────────────────────────────────────────────────
//
// `direcao` é "maior" quando mais é melhor e "menor" quando menos é
// melhor. `unidade` entra no rótulo da tela e do PDF.

export const TESTES = [
  {
    id: "bancoDeWells",
    rotulo: "Banco de Wells",
    unidade: "cm",
    direcao: "maior",
    capacidade: "Flexibilidade",
  },
  {
    id: "impulsaoHorizontal",
    rotulo: "Impulsão horizontal",
    unidade: "cm",
    direcao: "maior",
    capacidade: "Potência de membros inferiores",
  },
  {
    id: "impulsaoVertical",
    rotulo: "Impulsão vertical",
    unidade: "cm",
    direcao: "maior",
    capacidade: "Potência de membros inferiores",
  },
  {
    id: "potenciaMembrosSuperiores",
    rotulo: "Potência de membros superiores",
    unidade: "cm",
    direcao: "maior",
    capacidade: "Potência de membros superiores",
  },
  {
    id: "resistenciaAbdominal",
    rotulo: "Resistência abdominal",
    unidade: "rep/min",
    direcao: "maior",
    capacidade: "Resistência muscular localizada",
  },
  {
    // ── O circuito do tênis de mesa ────────────────────────
    //
    // A planilha chamava este teste de "teste de sprint", e o nome não
    // dizia o que ele é. O atleta parte de um sinal sonoro, corre até
    // pratos espalhados a distâncias diferentes, pega e deixa bolas
    // neles, muda de direção, e tem de seguir a ordem certa até o fim.
    // O cronômetro mede uma coisa só, o tempo total, mas o avaliador vê
    // cinco, e são cinco que nenhum outro teste da bateria alcança.
    //
    // Por isso o teste carrega critérios próprios. Eles não entram no
    // escore global e não se misturam com a avaliação técnica: são a
    // leitura deste teste, do mesmo jeito que o tempo de pista do BMX é
    // a leitura daquele. Quem dá as cinco notas é quem cronometrou.
    id: "circuito",
    rotulo: "Circuito de deslocamento",
    unidade: "s",
    direcao: "menor",
    capacidade: "Agilidade, coordenação e velocidade",
    // ── O protocolo ───────────────────────────────────────
    //
    // Vai gravado aqui porque o tempo só quer dizer alguma coisa se a
    // montagem for a mesma. Dois pratos meio metro mais perto mudam o
    // tempo de todos os atletas, e quem comparar agosto com janeiro não
    // teria como saber. Quem monta o teste em campo lê isto na tela e no
    // PDF da ficha de coleta.
    protocolo: {
      resumo:
        "Triângulo equilátero de 5 m de lado, com prato central na base "
        + "guardando as cinco bolas. O atleta parte do sinal sonoro, leva "
        + "uma bola por vez a cada um dos cinco pratos e depois recolhe "
        + "todas ao prato central, na ordem definida.",
      medidas: [
        "Base do triângulo: 5 m, com o prato central no meio dela",
        "Das duas extremidades da base ao ápice: 5 m",
        "Pratos intermediários: no meio de cada lado, a 2,5 m",
        "Cinco pratos na borda e um prato central, com cinco bolas",
      ],
      execucao: [
        "Largada ao sinal sonoro, com o atleta no prato central",
        "Pegar uma bola e deixá-la num prato, uma bola por viagem",
        "Depois dos cinco pratos servidos, recolher na mesma ordem",
        "O cronômetro para quando a última bola volta ao prato central",
      ],
      // A origem, porque o número só se compara com quem usa a mesma
      // régua. Não é o Faber puro: a coordenação apertou as distâncias
      // para um grupo que treina excelência, e por isso os tempos daqui
      // não se comparam com os publicados do teste original.
      origem:
        "Baseado no teste de Faber (2016) para detecção de talentos no "
        + "tênis de mesa, com adaptações da coordenação para elevar o "
        + "nível de exigência.",
    },
    criterios: [
      {
        id: "largada",
        rotulo: "Ação e reação na largada",
        detalhe: "A saída no sinal sonoro",
      },
      {
        id: "oculoManual",
        rotulo: "Coordenação óculo-manual",
        detalhe: "Pegar e deixar a bola no prato sem parar a corrida",
      },
      { id: "mudancaDeDirecao", rotulo: "Mudança de direção" },
      { id: "sprint", rotulo: "Sprint em distâncias variadas" },
      {
        id: "concentracao",
        rotulo: "Concentração na sequência",
        detalhe: "Seguir a ordem certa dos pratos até o fim",
      },
    ],
  },
  {
    id: "illinois",
    rotulo: "Teste de Illinois",
    unidade: "s",
    direcao: "menor",
    capacidade: "Agilidade",
  },
  {
    id: "toqueDeMesaLateral",
    rotulo: "Toque de mesa lateral",
    unidade: "toques",
    direcao: "maior",
    capacidade: "Agilidade de membros superiores",
  },
  {
    id: "tempoRetas",
    rotulo: "Tempo retas 1 e 2",
    unidade: "s",
    direcao: "menor",
    capacidade: "Desempenho em pista",
  },
  {
    id: "tempoPistaTotal",
    rotulo: "Tempo total de pista",
    unidade: "s",
    direcao: "menor",
    capacidade: "Desempenho em pista",
  },
];

export const TESTE_POR_ID = Object.fromEntries(TESTES.map(t => [t.id, t]));

// A bateria de cada modalidade, como aplicada até agosto de 2026.
//
// O Banco de Wells está na planilha das três modalidades e está vazio em
// todas: a coluna existe desde 2025 e não foi coletada em nenhuma das
// três datas de 2026. Fica listado aqui, e não removido, porque o que
// falta medir precisa aparecer em algum lugar — coluna vazia numa
// planilha não aparece.
export const BATERIAS = {
  BMX: ["bancoDeWells", "impulsaoHorizontal", "resistenciaAbdominal", "tempoRetas", "tempoPistaTotal"],
  Skate: ["bancoDeWells", "impulsaoHorizontal", "resistenciaAbdominal"],
  "Tênis de Mesa": ["bancoDeWells", "impulsaoHorizontal", "circuito"],
};

// ── A direção dos testes, em texto ─────────────────────
//
// A frase era escrita à mão e citava os testes das TRÊS modalidades de
// uma vez, então o relatório do tênis de mesa explicava o tempo de pista
// do BMX e o do skate explicava o circuito. Agora ela sai dos testes que
// a coleta realmente tem.
//
// Os rótulos vão como estão escritos, sem passar para minúscula: "Banco
// de Wells" e "Teste de Illinois" carregam nome de gente.
export function direcaoEmTexto(testes) {
  const emJuncao = nomes => (nomes.length > 1
    ? `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`
    : nomes[0]);
  const daDirecao = d => [...new Set(
    (testes || []).filter(x => x && x.direcao === d && x.rotulo).map(x => x.rotulo),
  )];

  const partes = [];
  const maiores = daDirecao("maior");
  const menores = daDirecao("menor");
  if (maiores.length) partes.push(`em ${emJuncao(maiores)}, mais é melhor`);
  if (menores.length) partes.push(`em ${emJuncao(menores)}, menos é melhor`);
  if (!partes.length) return "";
  const frase = partes.join("; ");
  return `${frase.charAt(0).toUpperCase()}${frase.slice(1)}.`;
}

export function bateriaDa(modalidade) {
  const ids = BATERIAS[modalidade] || [];
  return ids.map(id => TESTE_POR_ID[id]).filter(Boolean);
}

// ── As datas reais da primeira coleta de 2026 ────────────────
//
// A primeira coleta de 2026 aconteceu em janeiro, tênis de mesa em
// 09/01, BMX em 12/01 e skate em 13/01, e foi digitada meses depois com
// a data do dia da digitação.
//
// Quais datas são essas saiu do lote inteiro, e não de uma regra por
// mês. As 66 avaliações de 2026 caem em três ondas limpas:
//
//   primeira:  31/03 (13 atletas) e 01/04 (5, todos de tênis de mesa)
//   segunda:   27/04 (tênis de mesa e skate) e 29/04 (BMX)
//   terceira:  18/08 (tênis de mesa), 24/08 (skate), 26/08 (BMX)
//
// A segunda e a terceira estão certas e separam por modalidade do jeito
// esperado. Só a primeira está deslocada, e 01/04 entra nela junto com
// 31/03: são dias consecutivos, os cinco atletas de 01/04 são de tênis
// de mesa e quatro colegas de modalidade deles estão em 31/03.
//
// A prova de que a primeira onda é janeiro está no lote: a Alissa, de
// tênis de mesa, é a única com a data lançada certa, 08/01.
//
// Não é detalhe de rótulo. Toda conta de evolução divide por tempo, e
// trocar janeiro por março encurta o intervalo até agosto de sete meses
// para cinco. Pior: deixa a primeira coleta DEPOIS da segunda em alguns
// atletas, e a série inteira sai fora de ordem.
export const DATA_REAL_DA_PRIMEIRA_COLETA = {
  "Tênis de Mesa": "2026-01-09",
  BMX: "2026-01-12",
  Skate: "2026-01-13",
};

export const DATAS_DA_PRIMEIRA_ONDA = ["2026-03-31", "2026-04-01"];

export function corrigirDataDeColeta(data, modalidade) {
  const texto = String(data || "");
  if (!DATAS_DA_PRIMEIRA_ONDA.includes(texto)) return texto || null;
  return DATA_REAL_DA_PRIMEIRA_COLETA[modalidade] || texto;
}

// ── Evolução ─────────────────────────────────────────────────

function arred(n, casas = 1) {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  const f = Math.pow(10, casas);
  return Math.round(n * f) / f;
}

// Quanto o atleta melhorou, em por cento, entre duas coletas.
//
// O sinal é sempre o do desempenho, nunca o do número: positivo é
// melhora, negativo é piora, nos dois tipos de teste. Num teste de
// tempo, cair de 25,94 s para 24,97 s é melhora de 3,7% — e a conta
// ingênua `(novo - velho) / velho` daria −3,7%, que o relatório leria
// como piora e colocaria em vermelho.
export function evolucao(de, para, teste) {
  const a = lerResultado(de);
  const b = lerResultado(para);
  if (a.valor === null || b.valor === null) return null;
  if (a.valor === 0) return null;
  const bruta = ((b.valor - a.valor) / Math.abs(a.valor)) * 100;
  return arred(teste && teste.direcao === "menor" ? -bruta : bruta, 1);
}

// Melhorou, piorou ou ficou igual.
//
// A faixa morta de 1% existe porque a precisão dos testes não sustenta
// menos que isso: meio centímetro numa impulsão de 180 cm é 0,3% e é o
// erro da trena, não evolução do atleta. Sem a faixa, metade da equipe
// apareceria "evoluindo" em todo relatório.
export const FAIXA_MORTA = 1;

export function classificarEvolucao(percentual) {
  if (!Number.isFinite(percentual)) return null;
  if (percentual > FAIXA_MORTA) return "Melhorou";
  if (percentual < -FAIXA_MORTA) return "Piorou";
  return "Estável";
}

// A série de um teste para um atleta, ao longo das coletas.
//
// `coletas` é a lista de datas na ordem da planilha, e `valores` tem um
// bruto por coleta. A evolução de cada ponto é contra o ponto anterior
// que teve resultado, e não contra o imediatamente anterior: se o atleta
// faltou em abril, a comparação de agosto é com janeiro, porque é o
// último desempenho que existe. Comparar com uma ausência não devolveria
// nada, e o relatório mostraria o atleta sem evolução nenhuma por ter
// faltado uma vez.
export function serieDoTeste({ teste, coletas = [], valores = [] }) {
  let ultimoComValor = null;
  return coletas.map((coleta, i) => {
    const lido = resolverColeta(valores[i], teste);
    const variacao =
      lido.valor === null || ultimoComValor === null
        ? null
        : evolucao(ultimoComValor.valor, lido.valor, teste);
    const ponto = {
      coleta,
      valor: lido.valor,
      ausencia: lido.ausencia,
      tentativas: lido.tentativas,
      amplitude: lido.amplitude,
      compararamCom: lido.valor === null || ultimoComValor === null ? null : ultimoComValor.coleta,
      evolucao: variacao,
      classificacao: classificarEvolucao(variacao),
    };
    if (lido.valor !== null) ultimoComValor = { valor: lido.valor, coleta };
    return ponto;
  });
}

// Evolução total: do primeiro ao último resultado que existem.
//
// É o número que a coordenação pede, e ele não é a soma nem a média das
// evoluções parciais. Vem junto `coletasConsideradas`, porque "evoluiu
// 12%" significa coisas diferentes se foram oito meses ou se foram três
// semanas.
export function evolucaoTotal({ teste, coletas = [], valores = [] }) {
  const comValor = coletas
    .map((coleta, i) => ({ coleta, ...resolverColeta(valores[i], teste) }))
    .filter(p => p.valor !== null);
  if (comValor.length < 2) {
    return { percentual: null, de: null, para: null, coletasConsideradas: comValor.length };
  }
  const primeiro = comValor[0];
  const ultimo = comValor[comValor.length - 1];
  const percentual = evolucao(primeiro.valor, ultimo.valor, teste);
  return {
    percentual,
    classificacao: classificarEvolucao(percentual),
    de: primeiro.coleta,
    para: ultimo.coleta,
    coletasConsideradas: comValor.length,
  };
}

// ── Grupo ────────────────────────────────────────────────────

// A média de um teste na modalidade, numa coleta.
//
// Ignora ausência em vez de contá-la como zero, e devolve `n` junto, que
// é o que diz se a média vale algo: a média de agosto com dez atletas e
// a de janeiro com três não são comparáveis, e na planilha de 2026
// várias células de janeiro estão marcadas com "x".
export function mediaDaColeta(valores = [], teste = null) {
  const numeros = valores.map(v => resolverColeta(v, teste).valor).filter(v => v !== null);
  if (!numeros.length) return { media: null, n: 0 };
  const soma = numeros.reduce((s, v) => s + v, 0);
  return { media: arred(soma / numeros.length, 2), n: numeros.length };
}

// A média de evolução do grupo.
//
// É a média das evoluções individuais, e não a evolução das médias. As
// duas divergem quando atletas diferentes faltaram em coletas
// diferentes, que é o caso. A evolução das médias compararia um grupo de
// janeiro com outro grupo de agosto.
export function mediaDeEvolucao({ teste, atletas = [], coletas = [] }) {
  const percentuais = atletas
    .map(a => evolucaoTotal({ teste, coletas, valores: a.valores }).percentual)
    .filter(p => p !== null);
  if (!percentuais.length) return { media: null, n: 0 };
  const soma = percentuais.reduce((s, v) => s + v, 0);
  return { media: arred(soma / percentuais.length, 1), n: percentuais.length };
}

// ── Compilação de uma modalidade ─────────────────────────────

// Tudo que o relatório de uma modalidade precisa, a partir da planilha.
//
// `atletas` é `[{ nome, resultados: { <idDoTeste>: [bruto por coleta] } }]`.
export function compilarModalidade({ modalidade, coletas = [], atletas = [] }) {
  const bateria = bateriaDa(modalidade);
  const datas = coletas.map(c => corrigirDataDeColeta(c, modalidade));
  return {
    modalidade,
    coletas: datas,
    // As datas como estavam na origem, para a conferência contra a
    // planilha não virar caça ao fantasma.
    coletasComoLancadas: coletas,
    testes: bateria.map(teste => {
      const linhas = atletas.map(atleta => {
        const valores = (atleta.resultados || {})[teste.id] || [];
        return {
          nome: atleta.nome,
          pontos: serieDoTeste({ teste, coletas: datas, valores }),
          total: evolucaoTotal({ teste, coletas: datas, valores }),
          valores,
        };
      });
      return {
        ...teste,
        // Teste que está na bateria e não tem nenhum resultado em
        // nenhuma coleta. É o caso do Banco de Wells em 2026, e é um
        // achado do relatório, não um buraco a esconder: a coordenação
        // combinou medir flexibilidade e a flexibilidade não foi medida.
        semColeta: linhas.every(l => l.pontos.every(p => p.valor === null)),
        atletas: linhas,
        porColeta: datas.map((coleta, i) => ({
          coleta,
          ...mediaDaColeta(linhas.map(l => l.valores[i]), teste),
        })),
        mediaDeEvolucao: mediaDeEvolucao({
          teste,
          coletas: datas,
          atletas: linhas.map(l => ({ valores: l.valores })),
        }),
      };
    }),
  };
}

// ============================================================
// A segunda metade do módulo: a capacidade física avaliada por nota
//
// Até aqui, tudo neste arquivo é medida de aparelho: centímetro de
// trena, repetição contada, segundo de cronômetro. O que vem abaixo é a
// outra coisa que a planilha de performance chama de "física", e que não
// é a mesma: quatro notas de 1 a 10 que o preparador físico dá por
// capacidade, e que entram com peso de 25% no escore global.
//
// As duas vivem no mesmo arquivo porque são a mesma coleta. O preparador
// aplica a bateria de testes e dá as notas no mesmo dia, olhando para os
// resultados que acabou de anotar. Separar em dois módulos obrigaria a
// casar duas datas que nasceram juntas.
//
// ── A ponte entre as duas ───────────────────────────────────
//
// A planilha traz, no rodapé de cada aba, uma tabela que converte
// resultado de teste em faixa de nota, por modalidade e por idade. É ela
// que liga o número medido à nota dada, e é o motivo de a nota não ser
// palpite: "impulsão de 1,85 m num atleta de BMX de 13 anos sugere nota
// entre 6 e 8". Está em `CONVERSOES`, e a tela mostra a sugestão ao lado
// do campo, com a regra que a produziu.
//
// A sugestão não preenche a nota sozinha. Quem avalia é quem assina, e a
// tabela cobre dois testes por modalidade, enquanto as capacidades são
// quatro. Preencher automaticamente daria ar de medida ao que é olhar.
// ============================================================

// ── As faixas de idade da tabela de conversão ────────────────
//
// São as três da planilha, escritas nela como SUB 11, SUB 14 e 15+. A
// leitura segue a do esporte de base: sub 11 é quem ainda não fez 12,
// sub 14 vai dos 12 aos 14, e dos 15 em diante é a terceira.
export const FAIXAS_ETARIAS = [
  { id: "sub11", rotulo: "Sub 11", ate: 11 },
  { id: "sub14", rotulo: "Sub 14", ate: 14 },
  { id: "quinzeMais", rotulo: "15 anos ou mais", ate: Infinity },
];

// ── As faixas do circuito ────────────────────────────────────
//
// O circuito do tênis de mesa tem as suas, pedidas pela coordenação em
// 22/10/2026: 8 a 12, 13 a 16 e 17 a 21. Elas não substituem as de cima,
// que continuam valendo para a impulsão e para o Illinois; um teste pode
// ter o próprio corte etário, e este tem.
//
// Fora dos 8 aos 21 não há faixa, e sem faixa não há sugestão de nota. É
// o caso de quem tem 7 anos no programa: a tabela não fala dessa idade, e
// inventar um limiar para ela seria pior que não sugerir nada.
export const FAIXAS_DO_CIRCUITO = [
  { id: "oitoADoze", rotulo: "8 a 12 anos", de: 8, ate: 12 },
  { id: "trezeADezesseis", rotulo: "13 a 16 anos", de: 13, ate: 16 },
  { id: "dezesseteAVinteEUm", rotulo: "17 a 21 anos", de: 17, ate: 21 },
];

// Os testes que têm corte etário próprio.
export const FAIXAS_POR_TESTE = { circuito: FAIXAS_DO_CIRCUITO };

// A faixa de idade que vale para um teste. Sem teste, a do programa.
export function faixaEtariaDe(idade, teste) {
  const n = numeroOuNulo(idade);
  if (n === null) return null;
  const proprias = FAIXAS_POR_TESTE[teste];
  if (!proprias) return faixaEtaria(n);
  const anos = Math.floor(n);
  return proprias.find(f => anos >= f.de && anos <= f.ate) || null;
}

export function faixaEtaria(idade) {
  // `numeroOuNulo` e não `Number`, porque `Number("")` é zero e zero é
  // idade válida: a string vazia que vem de um campo em branco cairia em
  // sub 11 e a tela mostraria sugestão de nota para quem não tem data de
  // nascimento no cadastro.
  const n = numeroOuNulo(idade);
  if (n === null) return null;
  return FAIXAS_ETARIAS.find(f => Math.floor(n) <= f.ate) || FAIXAS_ETARIAS[2];
}

// ── As capacidades avaliadas por nota ────────────────────────
//
// Quatro por modalidade, de 1 a 10, na mesma escala da avaliação
// técnica: 1 a 3 regular, 4 a 7 intermediário, 8 a 10 avançado.
//
// A lista abaixo é a de agosto de 2026, e é só o modelo que a tela
// oferece: o que vale é a lista gravada dentro de cada avaliação. Isso
// não é zelo preventivo, é o que já aconteceu. As quatro capacidades
// mudaram de nome nas três modalidades entre abril e agosto de 2026, e
// ler a nota de janeiro com o rótulo de agosto diria que o atleta foi
// avaliado em velocidade quando ele foi avaliado em resistência
// aeróbica.
export const MODELOS_DE_CAPACIDADE = {
  // ── O tênis de mesa, com os nomes corrigidos ────────────
  //
  // A planilha escreveu a mesma capacidade de dois jeitos: "coordenação
  // óculo-manual" em janeiro e abril, "coordenação" em agosto. E chamou
  // de "resistência aeróbica" o que a comissão mede como velocidade.
  //
  // A coordenação desfez os dois em 22/10/2026: é "Coordenação motora"
  // nas três ondas, e é "Velocidade". Não é troca de nome na tela: ids
  // diferentes para a mesma capacidade quebram a série, e o gráfico de
  // janeiro a agosto sairia com quatro linhas interrompidas no meio em
  // vez de duas inteiras.
  "Tênis de Mesa": [
    { id: "agilidade", rotulo: "Agilidade", detalhe: "Mudança de direção específica" },
    { id: "reacao", rotulo: "Reação", detalhe: "Tempo de resposta" },
    { id: "coordenacaoMotora", rotulo: "Coordenação motora", detalhe: "Coordenação e propriocepção" },
    { id: "velocidade", rotulo: "Velocidade", detalhe: "Deslocamento curto" },
  ],
  Skate: [
    { id: "equilibrio", rotulo: "Equilíbrio" },
    { id: "coordenacao", rotulo: "Coordenação" },
    { id: "potencia", rotulo: "Potência" },
    { id: "controleCorporal", rotulo: "Controle corporal" },
  ],
  BMX: [
    { id: "potenciaExplosiva", rotulo: "Potência explosiva" },
    { id: "velocidade", rotulo: "Velocidade" },
    { id: "forcaAplicada", rotulo: "Força aplicada" },
    { id: "resistenciaDePotencia", rotulo: "Resistência de potência" },
  ],
};

// As listas que valeram em cada onda de 2026, para a importação gravar
// dentro de cada documento o que foi realmente avaliado naquele dia.
export const CAPACIDADES_HISTORICAS = {
  "2026-01": {
    // A ordem é a das COLUNAS da planilha de janeiro, que não é a de
    // agosto: lá a coordenação vem antes da reação. Os ids são os
    // mesmos das três ondas, e é isso que liga janeiro a agosto.
    "Tênis de Mesa": [
      { id: "agilidade", rotulo: "Agilidade" },
      { id: "coordenacaoMotora", rotulo: "Coordenação motora" },
      { id: "reacao", rotulo: "Reação" },
      { id: "velocidade", rotulo: "Velocidade" },
    ],
    Skate: [
      { id: "equilibrioDinamico", rotulo: "Equilíbrio dinâmico" },
      { id: "coordenacaoMotora", rotulo: "Coordenação motora" },
      { id: "potencia", rotulo: "Potência" },
      { id: "flexibilidade", rotulo: "Flexibilidade" },
    ],
    BMX: [
      { id: "potenciaMuscular", rotulo: "Potência muscular" },
      { id: "resistenciaAnaerobia", rotulo: "Resistência anaeróbia" },
      { id: "coordenacaoMotora", rotulo: "Coordenação motora" },
      { id: "flexibilidade", rotulo: "Flexibilidade" },
    ],
  },
};
CAPACIDADES_HISTORICAS["2026-04"] = CAPACIDADES_HISTORICAS["2026-01"];
CAPACIDADES_HISTORICAS["2026-08"] = MODELOS_DE_CAPACIDADE;

export function capacidadesSugeridas(modalidade) {
  return (MODELOS_DE_CAPACIDADE[modalidade] || []).map(c => ({ ...c }));
}

// As capacidades que valeram numa data, para a importação. Cai no modelo
// atual quando a data não é de nenhuma onda conhecida.
export function capacidadesDaOnda(modalidade, data) {
  const onda = String(data || "").slice(0, 7);
  const tabela = CAPACIDADES_HISTORICAS[onda] || MODELOS_DE_CAPACIDADE;
  return (tabela[modalidade] || MODELOS_DE_CAPACIDADE[modalidade] || []).map(c => ({ ...c }));
}

// ── A tabela de conversão ────────────────────────────────────
//
// Cada regra é lida de cima para baixo, e a primeira que serve é a que
// vale, que é como a planilha está escrita. `ate` compara por menor ou
// igual e serve aos testes de tempo, `de` compara por maior ou igual e
// serve aos de distância e repetição; a entrada sem nenhum dos dois é o
// resto.
//
// Os limiares de impulsão estão aqui em centímetros. Na planilha eles
// estão em metros, porque foram escritos à mão, e os resultados estão em
// centímetros na mesma aba. Converter na leitura, e não na tabela,
// deixaria a regra impressa no relatório divergindo do número medido.
export const CONVERSOES = {
  "Tênis de Mesa": {
    // ── O circuito, nas faixas de 8 a 12, 13 a 16 e 17 a 21 ──
    //
    // Os limiares são os que a coordenação já usava para este mesmo
    // teste, quando ele se chamava sprint, reencaixados nas três faixas
    // novas: a antiga sub 11 virou 8 a 12, a sub 14 virou 13 a 16 e a de
    // 15 em diante virou 17 a 21.
    //
    // Reencaixar muda quem cai em qual linha. Um atleta de 12 anos era
    // julgado pela régua da sub 14 e passa a ser julgado pela de 8 a 12,
    // que é um segundo mais folgada; um de 15 e um de 16 saem da régua de
    // 15 em diante e passam para a de 13 a 16, também mais folgada. São
    // os números que existem, e não medida nova: se a comissão tiver os
    // limiares dela para as faixas novas, é aqui que eles entram.
    oitoADoze: [
      {
        teste: "circuito",
        capacidade: "agilidade",
        texto: "Circuito: abaixo de 28 s dá 8 a 10, de 28 a 31 dá 6 a 7, de 31 a 34 dá 4 a 5, acima de 34 dá 3",
        faixas: [
          { ate: 28, nota: [8, 10] },
          { ate: 31, nota: [6, 7] },
          { ate: 34, nota: [4, 5] },
          { nota: [3, 3] },
        ],
      },
    ],
    trezeADezesseis: [
      {
        teste: "circuito",
        capacidade: "agilidade",
        texto: "Circuito: abaixo de 27 s dá 8 a 10, de 27 a 30 dá 6 a 7, de 30 a 33 dá 4 a 5, acima de 33 dá 3",
        faixas: [
          { ate: 27, nota: [8, 10] },
          { ate: 30, nota: [6, 7] },
          { ate: 33, nota: [4, 5] },
          { nota: [3, 3] },
        ],
      },
    ],
    dezesseteAVinteEUm: [
      {
        teste: "circuito",
        capacidade: "agilidade",
        texto: "Circuito: abaixo de 26 s dá 8 a 10, de 26 a 29 dá 6 a 7, de 29 a 32 dá 4 a 5, acima de 32 dá 3",
        faixas: [
          { ate: 26, nota: [8, 10] },
          { ate: 29, nota: [6, 7] },
          { ate: 32, nota: [4, 5] },
          { nota: [3, 3] },
        ],
      },
    ],
    sub11: [
      {
        teste: "impulsaoHorizontal",
        texto: "Impulsão: acima de 150 cm dá 7 a 9, de 120 a 150 dá 5 a 7, abaixo de 120 dá 3 a 5",
        faixas: [{ de: 150, nota: [7, 9] }, { de: 120, nota: [5, 7] }, { nota: [3, 5] }],
      },
    ],
    sub14: [
      {
        teste: "impulsaoHorizontal",
        texto: "Impulsão: acima de 160 cm dá 7 a 9, de 130 a 160 dá 5 a 7, abaixo de 130 dá 3 a 5",
        faixas: [{ de: 160, nota: [7, 9] }, { de: 130, nota: [5, 7] }, { nota: [3, 5] }],
      },
    ],
    quinzeMais: [
      {
        teste: "impulsaoHorizontal",
        texto: "Impulsão: acima de 170 cm dá 7 a 9, de 140 a 170 dá 5 a 7, abaixo de 140 dá 3 a 5",
        faixas: [{ de: 170, nota: [7, 9] }, { de: 140, nota: [5, 7] }, { nota: [3, 5] }],
      },
    ],
  },
  Skate: {
    sub11: [
      {
        teste: "illinois",
        texto: "Illinois: abaixo de 20,5 s dá 8 a 9, de 20,5 a 22 dá 6 a 7, acima de 22 dá 4 a 6",
        faixas: [{ ate: 20.5, nota: [8, 9] }, { ate: 22, nota: [6, 7] }, { nota: [4, 6] }],
      },
      {
        teste: "impulsaoHorizontal",
        capacidade: "potencia",
        texto: "Impulsão: acima de 140 cm dá 7 a 9, de 110 a 140 dá 5 a 7, abaixo de 110 dá 3 a 5",
        faixas: [{ de: 140, nota: [7, 9] }, { de: 110, nota: [5, 7] }, { nota: [3, 5] }],
      },
    ],
    sub14: [
      {
        teste: "illinois",
        texto: "Illinois: abaixo de 19,5 s dá 8 a 9, de 19,5 a 21 dá 6 a 7, acima de 21 dá 4 a 6",
        faixas: [{ ate: 19.5, nota: [8, 9] }, { ate: 21, nota: [6, 7] }, { nota: [4, 6] }],
      },
      {
        teste: "impulsaoHorizontal",
        capacidade: "potencia",
        texto: "Impulsão: acima de 150 cm dá 7 a 9, de 120 a 150 dá 5 a 7, abaixo de 120 dá 3 a 5",
        faixas: [{ de: 150, nota: [7, 9] }, { de: 120, nota: [5, 7] }, { nota: [3, 5] }],
      },
    ],
    quinzeMais: [
      {
        teste: "illinois",
        texto: "Illinois: abaixo de 18,5 s dá 8 a 9, de 18,5 a 20 dá 6 a 7, acima de 20 dá 4 a 6",
        faixas: [{ ate: 18.5, nota: [8, 9] }, { ate: 20, nota: [6, 7] }, { nota: [4, 6] }],
      },
      {
        teste: "impulsaoHorizontal",
        capacidade: "potencia",
        texto: "Impulsão: acima de 160 cm dá 7 a 9, de 130 a 160 dá 5 a 7, abaixo de 130 dá 3 a 5",
        faixas: [{ de: 160, nota: [7, 9] }, { de: 130, nota: [5, 7] }, { nota: [3, 5] }],
      },
    ],
  },
  BMX: {
    sub11: [
      {
        teste: "impulsaoHorizontal",
        capacidade: "potenciaExplosiva",
        texto: "Impulsão: acima de 160 cm dá 8 a 10, de 130 a 160 dá 6 a 8, abaixo de 130 dá 4 a 6",
        faixas: [{ de: 160, nota: [8, 10] }, { de: 130, nota: [6, 8] }, { nota: [4, 6] }],
      },
      {
        teste: "resistenciaAbdominal",
        capacidade: "resistenciaDePotencia",
        texto: "Abdominal: acima de 40 dá 9 a 10, de 30 a 40 dá 7 a 8, abaixo de 30 dá 5 a 7",
        faixas: [{ de: 40, nota: [9, 10] }, { de: 30, nota: [7, 8] }, { nota: [5, 7] }],
      },
    ],
    sub14: [
      {
        teste: "impulsaoHorizontal",
        capacidade: "potenciaExplosiva",
        texto: "Impulsão: acima de 180 cm dá 8 a 10, de 150 a 180 dá 6 a 8, abaixo de 150 dá 4 a 6",
        faixas: [{ de: 180, nota: [8, 10] }, { de: 150, nota: [6, 8] }, { nota: [4, 6] }],
      },
      {
        teste: "resistenciaAbdominal",
        capacidade: "resistenciaDePotencia",
        texto: "Abdominal: acima de 45 dá 9 a 10, de 35 a 45 dá 7 a 8, abaixo de 35 dá 5 a 7",
        faixas: [{ de: 45, nota: [9, 10] }, { de: 35, nota: [7, 8] }, { nota: [5, 7] }],
      },
    ],
    quinzeMais: [
      {
        teste: "impulsaoHorizontal",
        capacidade: "potenciaExplosiva",
        texto: "Impulsão: acima de 200 cm dá 8 a 10, de 170 a 200 dá 6 a 8, abaixo de 170 dá 4 a 6",
        faixas: [{ de: 200, nota: [8, 10] }, { de: 170, nota: [6, 8] }, { nota: [4, 6] }],
      },
      {
        teste: "resistenciaAbdominal",
        capacidade: "resistenciaDePotencia",
        texto: "Abdominal: acima de 50 dá 9 a 10, de 40 a 50 dá 7 a 8, abaixo de 40 dá 5 a 7",
        faixas: [{ de: 50, nota: [9, 10] }, { de: 40, nota: [7, 8] }, { nota: [5, 7] }],
      },
    ],
  },
};

// A faixa de nota que um resultado de teste sugere.
//
// Devolve a faixa inteira, e não um número: a tabela diz "6 a 8", e
// escolher 7 dentro disso é o julgamento do avaliador, que é justamente
// o que a nota subjetiva existe para registrar. Vem junto o texto da
// regra, para a tela poder mostrar de onde a sugestão saiu.
export function sugestaoDeNota({ modalidade, idade, teste, valor }) {
  const faixa = faixaEtariaDe(idade, teste);
  if (!faixa) return null;
  const regras = (CONVERSOES[modalidade] || {})[faixa.id] || [];
  const regra = regras.find(r => r.teste === teste);
  if (!regra) return null;
  const v = resolverColeta(valor, TESTE_POR_ID[teste]).valor;
  if (v === null) return null;
  const achada = regra.faixas.find(f => {
    if (Number.isFinite(f.ate)) return v <= f.ate;
    if (Number.isFinite(f.de)) return v >= f.de;
    return true;
  });
  if (!achada) return null;
  return {
    de: achada.nota[0],
    ate: achada.nota[1],
    capacidade: regra.capacidade || null,
    faixaEtaria: faixa.rotulo,
    regra: regra.texto,
    valor: v,
  };
}

// Todas as sugestões de uma coleta, indexadas pela capacidade que elas
// informam. Capacidade sem regra na tabela não aparece, e é a maioria:
// a tabela cobre dois testes por modalidade e as capacidades são quatro.
export function sugestoesDaColeta({ modalidade, idade, resultados = {} }) {
  const mapa = {};
  // Varre as regras de TODAS as faixas da modalidade, porque um teste
  // com corte etário próprio mora numa faixa que a idade do atleta não
  // alcança pelas faixas do programa. Quem decide se a regra vale é a
  // `sugestaoDeNota`, que escolhe o corte certo para cada teste.
  Object.values(CONVERSOES[modalidade] || {}).forEach(regras => {
    regras.forEach(r => {
      if (!r.capacidade || mapa[r.capacidade]) return;
      const s = sugestaoDeNota({ modalidade, idade, teste: r.teste, valor: resultados[r.teste] });
      if (s) mapa[r.capacidade] = { ...s, teste: r.teste };
    });
  });
  return mapa;
}

// ── Uma coleta compilada ─────────────────────────────────────
//
// Junta as duas metades num ponto só: os testes medidos, com a melhor
// tentativa já escolhida, e as notas de capacidade, com o somatório que
// entra no escore global com peso de 25%.
export function compilarTesteFisico({ modalidade, idade, coleta }) {
  const c = coleta || {};
  const mod = c.modalidade || modalidade || null;
  const resultados = c.resultados || {};
  const bateria = bateriaDa(mod);

  // Os critérios que alguns testes carregam, com a nota do avaliador.
  //
  // Eles são a leitura DO TESTE, e não capacidade do atleta: ficam fora
  // do somatório e fora do escore global. O circuito do tênis de mesa é
  // o caso: o cronômetro mede o tempo total, e quem aplicou viu cinco
  // coisas que nenhum outro teste da bateria alcança.
  const notasDoTeste = c.criteriosDoTeste || {};
  const testes = bateria.map(teste => {
    const lido = resolverColeta(resultados[teste.id], teste);
    const gravados = notasDoTeste[teste.id];
    const lista = Array.isArray(gravados) && gravados.length ? gravados : (teste.criterios || []);
    return {
      ...teste,
      ...lido,
      criterios: lista.map(x => {
        const base = (teste.criterios || []).find(y => y.id === x.id) || {};
        const nota = numeroOuNulo(x.nota);
        return {
          id: x.id,
          rotulo: x.rotulo || base.rotulo || x.id,
          detalhe: x.detalhe || base.detalhe || null,
          nota,
          faixa: faixaDaNotaDeCapacidade(nota),
        };
      }),
      sugestao: sugestaoDeNota({ modalidade: mod, idade, teste: teste.id, valor: resultados[teste.id] }),
    };
  });

  // Resultado lançado fora da bateria da modalidade. Acontece quando a
  // equipe aplica um teste a mais, e sumir com ele seria perder medida
  // que alguém foi a campo fazer.
  const extras = Object.keys(resultados)
    .filter(id => TESTE_POR_ID[id] && !bateria.some(t => t.id === id))
    .map(id => {
      const teste = TESTE_POR_ID[id];
      return { ...teste, ...resolverColeta(resultados[id], teste), foraDaBateria: true };
    });

  const lista = Array.isArray(c.capacidades) && c.capacidades.length
    ? c.capacidades
    : capacidadesSugeridas(mod);
  const capacidades = lista.map(cap => {
    const nota = numeroOuNulo(cap.nota);
    return { ...cap, nota, faixa: faixaDaNotaDeCapacidade(nota) };
  });
  const comNota = capacidades.filter(x => Number.isFinite(x.nota));
  const divisor = numeroOuNulo(c.divisor) > 0 ? numeroOuNulo(c.divisor) : lista.length;
  const somatorio = comNota.length ? arred(comNota.reduce((s, x) => s + x.nota, 0), 2) : null;

  const todos = [...testes, ...extras];
  return {
    data: c.data || null,
    modalidade: mod,
    avaliador: c.avaliador || null,
    observacoes: c.observacoes || "",
    idade: Number.isFinite(Number(idade)) ? Number(idade) : null,
    faixaEtaria: faixaEtaria(idade),
    testes: todos,
    capacidades,
    somatorio,
    divisor,
    maximo: divisor * 10,
    media: somatorio !== null && divisor ? arred(somatorio / divisor, 2) : null,
    completa: lista.length > 0 && comNota.length === lista.length
      && todos.every(t => t.valor !== null || t.ausencia !== "semRegistro"),
    faltam: [
      ...todos.filter(t => t.valor === null && t.ausencia === "semRegistro").map(t => t.rotulo),
      ...todos.flatMap(t => (t.criterios || [])
        .filter(x => !Number.isFinite(x.nota))
        .map(x => `${x.rotulo} (${t.rotulo.toLowerCase()})`)),
      ...capacidades.filter(x => !Number.isFinite(x.nota)).map(x => x.rotulo),
    ],
  };
}

// A mesma escala da avaliação técnica. Está repetida aqui, e não
// importada, porque `avaliacaoTecnica.js` não deve virar dependência de
// quem só quer ler teste de campo.
const FAIXAS_DE_CAPACIDADE = [
  { id: "regular", ate: 3, rotulo: "Regular" },
  { id: "intermediario", ate: 7, rotulo: "Intermediário" },
  { id: "avancado", ate: 10, rotulo: "Avançado" },
];

// A borda é a mesma de `faixaDaNota`, em avaliacaoTecnica.js, e segue a
// convenção da própria planilha: as faixas estão escritas com inteiros,
// "4-7", e no quadro do escore global a mesma coisa aparece por extenso
// como 5,0 a 6,9. Logo, 7,5 ainda é intermediário, e não avançado.
export function faixaDaNotaDeCapacidade(nota) {
  if (!Number.isFinite(nota)) return null;
  return FAIXAS_DE_CAPACIDADE.find(f => nota < f.ate + 1) || FAIXAS_DE_CAPACIDADE[2];
}

// ── A série de um atleta ─────────────────────────────────────
//
// Uma linha por coleta, em ordem de data, com a evolução de cada teste
// contra a coleta anterior QUE TEVE RESULTADO e contra a primeira. A
// regra de pular ausência é a mesma de `serieDoTeste`, e pelo mesmo
// motivo: quem faltou em abril é comparado com janeiro, e não aparece
// sem evolução nenhuma por ter faltado uma vez.
export function compilarSerieDeTestes({ modalidade, idadeEm: idadeNaData, coletas = [] }) {
  const ordenadas = [...coletas]
    .filter(c => c && c.data)
    .sort((a, b) => String(a.data).localeCompare(String(b.data)));

  const ultimoPorTeste = {};
  const primeiroPorTeste = {};
  let primeiraMedia = null;
  let mediaAnterior = null;

  return ordenadas.map(coleta => {
    const idade = typeof idadeNaData === "function" ? idadeNaData(coleta.data) : idadeNaData;
    const ponto = compilarTesteFisico({ modalidade, idade, coleta });
    const testes = ponto.testes.map(t => {
      const anterior = ultimoPorTeste[t.id] || null;
      const primeiro = primeiroPorTeste[t.id] || null;
      const variacao = t.valor !== null && anterior ? evolucao(anterior.valor, t.valor, t) : null;
      const desdeOInicio = t.valor !== null && primeiro ? evolucao(primeiro.valor, t.valor, t) : null;
      if (t.valor !== null) {
        ultimoPorTeste[t.id] = { valor: t.valor, coleta: coleta.data };
        if (!primeiroPorTeste[t.id]) primeiroPorTeste[t.id] = { valor: t.valor, coleta: coleta.data };
      }
      return {
        ...t,
        compararamCom: anterior ? anterior.coleta : null,
        evolucao: variacao,
        classificacao: classificarEvolucao(variacao),
        desdeOInicio,
        classificacaoDesdeOInicio: classificarEvolucao(desdeOInicio),
        dataDoInicio: primeiro ? primeiro.coleta : null,
      };
    });

    const delta = Number.isFinite(ponto.media) && Number.isFinite(mediaAnterior)
      ? arred(ponto.media - mediaAnterior, 2)
      : null;
    const desdeOInicio = Number.isFinite(ponto.media) && Number.isFinite(primeiraMedia)
      ? arred(ponto.media - primeiraMedia, 2)
      : null;
    if (Number.isFinite(ponto.media)) {
      if (primeiraMedia === null) primeiraMedia = ponto.media;
      mediaAnterior = ponto.media;
    }

    return { ...ponto, testes, delta, desdeOInicio };
  });
}

// ── A leitura em texto ───────────────────────────────────────
//
// O que mudou nos testes desta coleta para a anterior, em uma frase.
// Mede o que a tabela de números já mostra, e serve ao PDF e a quem lê
// de longe: três testes melhoraram, um piorou, e o que piorou tem nome.
export function laudoDosTestes(ponto) {
  if (!ponto) return null;
  const comparaveis = ponto.testes.filter(t => Number.isFinite(t.evolucao));
  const medidos = ponto.testes.filter(t => t.valor !== null);
  if (!medidos.length) return null;

  const partes = [];
  if (comparaveis.length) {
    const melhoraram = comparaveis.filter(t => t.classificacao === "Melhorou");
    const pioraram = comparaveis.filter(t => t.classificacao === "Piorou");
    const estaveis = comparaveis.filter(t => t.classificacao === "Estável");
    const pedacos = [];
    if (melhoraram.length) {
      pedacos.push(`${melhoraram.length} ${melhoraram.length === 1 ? "melhorou" : "melhoraram"}`);
    }
    if (estaveis.length) {
      pedacos.push(`${estaveis.length} ${estaveis.length === 1 ? "ficou estável" : "ficaram estáveis"}`);
    }
    if (pioraram.length) {
      pedacos.push(`${pioraram.length} ${pioraram.length === 1 ? "piorou" : "pioraram"}`);
    }
    partes.push(
      `Dos ${comparaveis.length} testes com resultado nas duas coletas, ${pedacos.join(", ")}.`,
    );
    if (pioraram.length) {
      partes.push(
        `Em queda: ${pioraram.map(t => `${t.rotulo.toLowerCase()} (${sinal(t.evolucao)}%)`).join(", ")}.`,
      );
    }
  } else {
    partes.push("Esta é a primeira coleta com resultado, e ainda não há com o que comparar.");
  }

  const instaveis = medidos.filter(t => t.tentativas.length > 1 && t.amplitude !== null
    && t.valor !== 0 && Math.abs(t.amplitude / t.valor) * 100 > 10);
  if (instaveis.length) {
    partes.push(
      `Coleta instável em ${instaveis.map(t => t.rotulo.toLowerCase()).join(", ")}: as três `
      + "tentativas ficaram longe entre si, e a melhor pode ser a tentativa boa e não o nível do atleta.",
    );
  }

  return {
    texto: partes.join(" "),
    ressalva:
      `Variação abaixo de ${FAIXA_MORTA}% é lida como estável, porque é o erro do próprio `
      + "instrumento e não evolução do atleta.",
  };
}

function sinal(n) {
  if (!Number.isFinite(n)) return "";
  // O sinal de menos é o tipográfico, o mesmo que as tabelas da tela
  // usam. O hífen do teclado é mais estreito e mais alto, e ao lado de um
  // número ele parece um traço de união e não um valor negativo.
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${String(Math.abs(n)).replace(".", ",")}`;
}

function numeroOuNulo(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}
